import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { LoadingCount } from "@volca/ui/components/ui/loading-count";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { useTabNavigate } from "@/app-shell";
import { SETTINGS_PAGE_TITLES } from "@/business-settings/constants";
import { TEXT_MESSAGE_TAB_LABELS } from "@/text-messages/constants";
import { CONVERSATION_TYPES } from "@/text-messages/constants";
import {
  useGetInboundTextMessages,
  useGetOptedOutTextMessages,
  useGetOutboundTextMessages,
} from "@/text-messages/query";
import { ConversationMessages } from "./text-message-tabs/conversation-messages";
import { OptedOutMessages } from "./text-message-tabs/opted-out-messages";
import { OutboundMessages } from "./text-message-tabs/outbound-messages";

type TextMessageTab = keyof typeof CONVERSATION_TYPES;

const TAB_SEGMENT: Partial<Record<TextMessageTab, string>> = {
  conversation: "conversations",
  outbound: "outbound",
  opted_out: "opted-out",
};
const tabFromSegment = (segment?: string): TextMessageTab =>
  (Object.entries(TAB_SEGMENT).find(([, value]) => value === segment)?.[0] as
    | TextMessageTab
    | undefined) ?? "conversation";

const TextMessages = () => {
  const { businessId, tab } = useParams();
  const tabNavigate = useTabNavigate();
  const activeTab = tabFromSegment(tab);
  const setActiveTab = (next: TextMessageTab) => {
    const path = `/business-settings/${businessId}/text-messages/${TAB_SEGMENT[next]}`;
    tabNavigate(path);
  };

  const { data: inboundMessages, isLoading: isLoadingInbound } =
    useGetInboundTextMessages(businessId);

  const { data: outboundMessages, isLoading: isLoadingOutbound } =
    useGetOutboundTextMessages(businessId);

  const { data: optedOutMessages, isLoading: isLoadingOptedOut } =
    useGetOptedOutTextMessages(businessId);

  const messageCounts = useMemo(
    () => ({
      inbound: inboundMessages?.length || 0,
      outbound: outboundMessages?.length || 0,
      opted_out: optedOutMessages?.length || 0,
    }),
    [inboundMessages, outboundMessages, optedOutMessages],
  );

  return (
    <PageContainer variant="workspace">
      <PageHeader title={SETTINGS_PAGE_TITLES["text-messages"]} />
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as TextMessageTab)}
        className="flex min-h-0 w-full flex-1 flex-col gap-4"
      >
        <TabsList>
          <TabsTrigger value={CONVERSATION_TYPES.conversation}>
            {TEXT_MESSAGE_TAB_LABELS[CONVERSATION_TYPES.conversation]}
            <LoadingCount
              count={messageCounts.inbound}
              isCountLoading={isLoadingInbound}
              size="small"
            />
          </TabsTrigger>
          <TabsTrigger value={CONVERSATION_TYPES.outbound}>
            {TEXT_MESSAGE_TAB_LABELS[CONVERSATION_TYPES.outbound]}
            <LoadingCount
              count={messageCounts.outbound}
              isCountLoading={isLoadingOutbound}
              size="small"
            />
          </TabsTrigger>
          <TabsTrigger value={CONVERSATION_TYPES.opted_out}>
            {TEXT_MESSAGE_TAB_LABELS[CONVERSATION_TYPES.opted_out]}
            <LoadingCount
              count={messageCounts.opted_out}
              isCountLoading={isLoadingOptedOut}
              size="small"
            />
          </TabsTrigger>
        </TabsList>
        <PageMainCard>
          <PageMainStack className="min-h-0 flex-1">
            <TabsContent
              value={CONVERSATION_TYPES.conversation}
              className="min-h-0 w-full flex-1 overflow-hidden pt-4"
            >
              <ConversationMessages />
            </TabsContent>
            <TabsContent
              value={CONVERSATION_TYPES.outbound}
              className="min-h-0 w-full flex-1 overflow-hidden pt-4"
            >
              <OutboundMessages />
            </TabsContent>
            <TabsContent
              value={CONVERSATION_TYPES.opted_out}
              className="min-h-0 w-full flex-1 overflow-hidden pt-4"
            >
              <OptedOutMessages />
            </TabsContent>
          </PageMainStack>
        </PageMainCard>
      </Tabs>
    </PageContainer>
  );
};

export default TextMessages;
